import type { Locale as AntdLocale } from "antd/lib/locale-provider";
import enUS from "antd/lib/locale/en_US";
import esES from "antd/lib/locale/es_ES";
import kkKZ from "antd/lib/locale/kk_KZ";
import ruRU from "antd/lib/locale/ru_RU";
// `moment` ships English built in; the other three have to be registered
// before `moment.locale()` will accept them. The DatePicker in the person
// editors reads whichever one is active.
import "moment/locale/es";
import "moment/locale/kk";
import "moment/locale/ru";
import { en } from "./messages/en";
import { es } from "./messages/es";
import { kk } from "./messages/kk";
import { ru } from "./messages/ru";
import { Locale, LOCALE_CODES, Messages } from "./types";

export interface LocaleDescriptor {
    code: Locale;
    /** The language's own name — never translated, so it reads in every UI. */
    label: string;
    /** Two letters for the switcher's collapsed state. */
    short: string;
    /** BCP 47 tag for `<html lang>`, `Intl` and `toLocaleString`. */
    tag: string;
    messages: Messages;
    antd: AntdLocale;
    /** Identifier `moment.locale()` expects. */
    moment: string;
}

const DESCRIPTORS: Record<Locale, LocaleDescriptor> = {
    en: {
        code: "en",
        label: "English",
        short: "EN",
        tag: "en",
        messages: en,
        antd: enUS,
        moment: "en"
    },
    ru: {
        code: "ru",
        label: "Русский",
        short: "RU",
        tag: "ru",
        messages: ru,
        antd: ruRU,
        moment: "ru"
    },
    kk: {
        code: "kk",
        label: "Қазақша",
        short: "KK",
        tag: "kk",
        messages: kk,
        antd: kkKZ,
        moment: "kk"
    },
    es: {
        code: "es",
        label: "Español",
        short: "ES",
        tag: "es",
        messages: es,
        antd: esES,
        moment: "es"
    }
};

/** The switcher's order — English first, then the rest as listed. */
export const LOCALES: LocaleDescriptor[] = LOCALE_CODES.map(code => DESCRIPTORS[code]);

export const DEFAULT_LOCALE: Locale = "en";

export const describeLocale = (locale: Locale): LocaleDescriptor => DESCRIPTORS[locale];

/** Narrows an arbitrary string to a locale this console actually has. */
export function asLocale(value: string | null | undefined): Locale | undefined {
    if (value == null) return undefined;
    const primary = value.trim().toLowerCase().split(/[-_]/)[0];
    return LOCALE_CODES.find(code => code === primary);
}

/**
 * The locale to start in: the stored preference, else the closest match among
 * the browser's languages, else English.
 */
export function detectLocale(stored?: string | null): Locale {
    const remembered = asLocale(stored);
    if (remembered !== undefined) return remembered;

    const preferences = navigator.languages ?? [navigator.language];
    for (const preference of preferences) {
        const match = asLocale(preference);
        if (match !== undefined) return match;
    }
    return DEFAULT_LOCALE;
}
